import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  useTheme,
  Chip,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import {
  getSports,
  createSport,
  updateSport,
  toggleSportStatus,
} from "service/admin/contentAdminService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EMPTY_FORM = {
  title: "",
  icon: "",
  cover_image: "",
  is_active: true,
};

const SportForm = ({ open, onClose, editData, onSaved }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editData) {
        setFormData({
          title: editData.title || "",
          icon: editData.icon || "",
          cover_image: editData.cover_image || "",
          is_active: editData.is_active !== undefined ? editData.is_active : true,
        });
      } else {
        setFormData(EMPTY_FORM);
      }
    }
  }, [open, editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({ ...prev, is_active: e.target.checked }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    try {
      setSaving(true);
      if (editData) {
        await updateSport(editData.id, formData);
        toast.success("Sport updated successfully!");
      } else {
        await createSport(formData);
        toast.success("Sport created successfully!");
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          `Failed to ${editData ? "update" : "create"} sport.`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{editData ? "Edit Sport" : "Add Sport"}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            color="secondary"
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Icon (Firebase path)"
            name="icon"
            value={formData.icon}
            onChange={handleChange}
            color="secondary"
            fullWidth
            size="small"
          />
          <TextField
            label="Cover Image (Firebase path)"
            name="cover_image"
            value={formData.cover_image}
            onChange={handleChange}
            color="secondary"
            fullWidth
            size="small"
          />
          <FormControlLabel
            control={
              <Switch
                color="secondary"
                checked={!!formData.is_active}
                onChange={handleSwitchChange}
              />
            }
            label={formData.is_active ? "Active" : "Inactive"}
            sx={{ color: theme.palette.secondary[200] }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? "Saving..." : editData ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Sports = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getSports();
      const data = response.data.data;
      setRows(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      toast.error("Failed to load sports.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      await toggleSportStatus(id, newStatus);
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, is_active: newStatus } : r))
      );
      toast.success(`Sport ${newStatus ? "activated" : "deactivated"}.`);
    } catch (error) {
      toast.error("Failed to update sport status.");
    }
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.NO",
      flex: 0.4,
      sortable: false,
      renderCell: (params) => params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "icon",
      headerName: "ICON",
      flex: 0.6,
      sortable: false,
      renderCell: (params) =>
        params.value ? (
          <Box
            component="img"
            src={params.value}
            alt="icon"
            sx={{ width: 28, height: 28, objectFit: "contain" }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          "-"
        ),
    },
    {
      field: "title",
      headerName: "TITLE",
      flex: 1.5,
    },
    {
      field: "is_active",
      headerName: "ACTIVE",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          size="small"
          color={params.value ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton
            size="small"
            color="secondary"
            onClick={() => {
              setEditData(params.row);
              setFormOpen(true);
            }}
            title="Edit"
          >
            <Edit fontSize="small" />
          </IconButton>
          <Switch
            color="secondary"
            size="small"
            checked={!!params.row.is_active}
            onChange={() =>
              handleToggleStatus(params.row.id, !!params.row.is_active)
            }
          />
        </Box>
      ),
    },
  ];

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <FlexBetween mb={1}>
          <Header title="SPORTS MASTER" subTitle="Manage sports categories" />
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddCircleIcon />}
            onClick={() => {
              setEditData(null);
              setFormOpen(true);
            }}
          >
            Add Sport
          </Button>
        </FlexBetween>

        <Box
          mt="10px"
          height="70vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <DataGrid
            loading={isLoading}
            getRowId={(row) => row.id}
            rows={rows}
            columns={columns}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            components={{ Toolbar: DataGridCustomToolbar }}
            sx={{
              "& .MuiCircularProgress-circle": {
                color: theme.palette.secondary[300],
              },
            }}
          />
        </Box>
      </Box>

      <SportForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        editData={editData}
        onSaved={fetchData}
      />
    </>
  );
};

export default Sports;
